/**
 * Customer Group Service
 * Ügyfélcsoportok kezelése Firestore-ban
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  writeBatch,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { CustomerGroup, CustomerGroupStats, BulkOperationResult } from '../types/customerGroup';

const CUSTOMER_GROUPS_COLLECTION = 'customerGroups';
const CUSTOMERS_COLLECTION = 'customers';

/**
 * Összes csoport lekérése egy szervezethez
 */
export async function getCustomerGroups(organizationId: string): Promise<CustomerGroup[]> {
  try {
    const q = query(
      collection(db, CUSTOMER_GROUPS_COLLECTION),
      where('organizationId', '==', organizationId),
      where('isActive', '==', true),
      orderBy('name', 'asc')
    );

    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || doc.data().createdAt,
      updatedAt: doc.data().updatedAt?.toDate?.()?.toISOString() || doc.data().updatedAt,
      lastUsedAt: doc.data().lastUsedAt?.toDate?.()?.toISOString() || doc.data().lastUsedAt
    })) as CustomerGroup[];
  } catch (error) {
    console.error('Error fetching customer groups:', error);
    throw error;
  }
}

/**
 * Egy csoport lekérése ID alapján
 */
export async function getCustomerGroup(groupId: string): Promise<CustomerGroup | null> {
  try {
    const docRef = doc(db, CUSTOMER_GROUPS_COLLECTION, groupId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
      updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt,
      lastUsedAt: data.lastUsedAt?.toDate?.()?.toISOString() || data.lastUsedAt
    } as CustomerGroup;
  } catch (error) {
    console.error('Error fetching customer group:', error);
    throw error;
  }
}

/**
 * Új csoport létrehozása
 */
export async function createCustomerGroup(
  group: Omit<CustomerGroup, 'id' | 'createdAt' | 'updatedAt' | 'customerCount'>
): Promise<string> {
  try {
    const now = Timestamp.now();

    const groupData: any = {
      ...group,
      customerCount: group.customerIds?.length || 0,
      createdAt: now,
      updatedAt: now,
      isActive: true,
      campaignCount: 0
    };

    // Remove undefined values to prevent Firestore errors
    Object.keys(groupData).forEach(key => {
      if (groupData[key] === undefined) {
        delete groupData[key];
      }
    });

    const docRef = await addDoc(collection(db, CUSTOMER_GROUPS_COLLECTION), groupData);

    return docRef.id;
  } catch (error) {
    console.error('Error creating customer group:', error);
    throw error;
  }
}

/**
 * Csoport frissítése
 */
export async function updateCustomerGroup(
  groupId: string,
  updates: Partial<Omit<CustomerGroup, 'id' | 'createdAt' | 'organizationId' | 'createdBy'>>
): Promise<void> {
  try {
    const docRef = doc(db, CUSTOMER_GROUPS_COLLECTION, groupId);

    const updateData: any = {
      ...updates,
      updatedAt: Timestamp.now()
    };

    // Ha customerIds frissül, frissítjük a számlálót is
    if (updates.customerIds) {
      updateData.customerCount = updates.customerIds.length;
    }

    // Remove undefined values to prevent Firestore errors
    Object.keys(updateData).forEach(key => {
      if (updateData[key] === undefined) {
        delete updateData[key];
      }
    });

    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error('Error updating customer group:', error);
    throw error;
  }
}

/**
 * Csoport törlése (soft delete)
 */
export async function deleteCustomerGroup(groupId: string): Promise<void> {
  try {
    const docRef = doc(db, CUSTOMER_GROUPS_COLLECTION, groupId);

    await updateDoc(docRef, {
      isActive: false,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error deleting customer group:', error);
    throw error;
  }
}

/**
 * Ügyfelek hozzáadása csoporthoz
 */
export async function addCustomersToGroup(
  groupId: string,
  customerIds: string[]
): Promise<void> {
  try {
    const docRef = doc(db, CUSTOMER_GROUPS_COLLECTION, groupId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Customer group not found');
    }

    const currentCustomerIds = docSnap.data().customerIds || [];
    const newCustomerIds = Array.from(new Set([...currentCustomerIds, ...customerIds]));

    await updateDoc(docRef, {
      customerIds: newCustomerIds,
      customerCount: newCustomerIds.length,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error adding customers to group:', error);
    throw error;
  }
}

/**
 * Ügyfelek eltávolítása csoportból
 */
export async function removeCustomersFromGroup(
  groupId: string,
  customerIds: string[]
): Promise<void> {
  try {
    const docRef = doc(db, CUSTOMER_GROUPS_COLLECTION, groupId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Customer group not found');
    }

    const currentCustomerIds = docSnap.data().customerIds || [];
    const newCustomerIds = currentCustomerIds.filter(
      (id: string) => !customerIds.includes(id)
    );

    await updateDoc(docRef, {
      customerIds: newCustomerIds,
      customerCount: newCustomerIds.length,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error removing customers from group:', error);
    throw error;
  }
}

/**
 * Csoport statisztika lekérése
 */
export async function getCustomerGroupStats(
  groupId: string,
  organizationId: string
): Promise<CustomerGroupStats> {
  try {
    const group = await getCustomerGroup(groupId);

    if (!group) {
      throw new Error('Customer group not found');
    }

    // Ügyfelek lekérése
    const customersQuery = query(
      collection(db, CUSTOMERS_COLLECTION),
      where('organizationId', '==', organizationId),
      where('__name__', 'in', group.customerIds.slice(0, 10)) // Firestore limit: max 10
    );

    const customersSnap = await getDocs(customersQuery);

    let activeCount = 0;
    let withPhoneCount = 0;
    let withEmailCount = 0;

    customersSnap.docs.forEach(doc => {
      const data = doc.data();
      if (data.status === 'active') activeCount++;
      if (data.phone) withPhoneCount++;
      if (data.email) withEmailCount++;
    });

    return {
      totalCustomers: group.customerCount,
      activeCustomers: activeCount,
      withPhoneNumber: withPhoneCount,
      withEmail: withEmailCount,
      lastCampaignDate: group.lastUsedAt,
      totalCampaigns: group.campaignCount || 0
    };
  } catch (error) {
    console.error('Error fetching customer group stats:', error);
    throw error;
  }
}

/**
 * Csoport használat frissítése (kampány indításkor)
 */
export async function updateGroupUsage(groupId: string): Promise<void> {
  try {
    const docRef = doc(db, CUSTOMER_GROUPS_COLLECTION, groupId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const currentCount = docSnap.data().campaignCount || 0;
      await updateDoc(docRef, {
        lastUsedAt: Timestamp.now(),
        campaignCount: currentCount + 1
      });
    }
  } catch (error) {
    console.error('Error updating group usage:', error);
    // Ne dobjunk hibát, ez nem kritikus
  }
}

/**
 * Ügyfelek exportálása csoportból
 */
export async function exportCustomerGroup(
  groupId: string,
  organizationId: string
): Promise<any[]> {
  try {
    const group = await getCustomerGroup(groupId);

    if (!group) {
      throw new Error('Customer group not found');
    }

    // Batch lekérés (max 10-es csoportokban)
    const batchSize = 10;
    const batches = [];

    for (let i = 0; i < group.customerIds.length; i += batchSize) {
      const batch = group.customerIds.slice(i, i + batchSize);
      const q = query(
        collection(db, CUSTOMERS_COLLECTION),
        where('organizationId', '==', organizationId),
        where('__name__', 'in', batch)
      );
      batches.push(getDocs(q));
    }

    const results = await Promise.all(batches);
    const customers: any[] = [];

    results.forEach(snapshot => {
      snapshot.docs.forEach(doc => {
        customers.push({
          id: doc.id,
          ...doc.data()
        });
      });
    });

    return customers;
  } catch (error) {
    console.error('Error exporting customer group:', error);
    throw error;
  }
}
